import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store/store";
import React, { useCallback, useEffect, useState } from "react";
import {
  fetchApplicationById,
  setAppId,
} from "../../store/slices/applicationDetailsSlice";
import { useNavigate, useParams } from "react-router-dom";
import CustomBreadcrumb from "../CustomBreadcrumb/CustomBreadcrumb";
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { triggerToast } from "../../utils/toast";
import {
  Button,
  Dropdown,
  LoadingSpinner,
  TextArea,
} from "cprime-foundational-components";
import { axiosInstance } from "../../utils/axiosInstance";
import * as style from "./Publish.module.scss";
import { restoreVersion } from "../../services/RequestAccess/RequestAccess";
import RoleComparison from "./RoleComparison";

const CompareApplication = () => {
  const { id } = useParams();
  const { data } = useSelector((state: RootState) => state?.applicationDetails);

  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const scssClass = style as any;

  const [formData, setFormData] = useState<any>({
    app1: "",
    app2: "",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [contextForm, setContextForm] = useState<any>({
    comment: "",
  });

  const [publishedApplicationList, setPublishedApplicationList] = useState<
    any[]
  >([]);

  const [selectedApp, setSelectedApp] = useState<string | null>(null);

  const handleChange = (field: string, value: string) => {
    setContextForm({ ...contextForm, [field]: value });
  };

  const fetchPublishedApplications = useCallback(
    async (applicationId: number) => {
      const response = await axiosInstance.get(
        `/publish/all-versions/${applicationId}`,
      );
      setPublishedApplicationList(
        response?.data?.data?.versions?.map((item: any) => ({
          value: item.id,
          label: `Published on ${item?.publishedAt} by ${item?.publishedBy}`,
        })),
      );
    },
    [],
  );

  useEffect(() => {
    if (id) {
      const applicationId = id?.split("-")?.[1];
      if (applicationId) {
        dispatch(fetchApplicationById(Number(applicationId)) as any);
        dispatch(setAppId(Number(applicationId)));
        fetchPublishedApplications(Number(applicationId));
      }
    }
  }, [dispatch, fetchPublishedApplications, id]);

  const isFormValid = () => {
    return !(
      contextForm?.comment &&
      selectedApp &&
      formData?.app1 &&
      formData?.app2
    );
  };

  const restorePublishVersion = async () => {
    try {
      if (!contextForm?.comment) return;
      const res = await restoreVersion(
        {
          // ...(uploadId && { uploadId }),
          comment: contextForm?.comment,
          requiresApproval: true,
        },
        selectedApp === "app1" ? formData?.app1 : formData?.app2,
      );
      if (res?.status === "SUCCESS") {
        triggerToast(res?.message, "success");
        navigate(`/application-list/app-${data?.id}`, {
          state: { selectedTab: 9 },
        });
      } else {
        triggerToast(res?.message, "error");
      }
    } catch (err: any) {
      triggerToast(err?.message || "Something went wrong", "error");
    }
  };

  return (
    <>
      <div
        style={{
          width: "calc(100% - 4rem)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          margin: "2rem 2rem 0 2rem",
        }}
      >
        <CustomBreadcrumb
          items={[
            {
              label: "Application List",
              href: "/application-list",
              icon: <HomeOutlinedIcon sx={{ color: "#2469FF" }} />,
            },
            {
              label: `${data?.app_cmdb_name}`,
              href: `/application-list/app-${data?.id}`,
            },
            {
              label: `Compare`,
              href: undefined,
            },
          ]}
        />

        <Box sx={{ maxHeight: "calc(100vh - 220x)", overflowY: "auto" }}>
          <Box
            sx={{
              background: "white",
              marginTop: "1rem",
              padding: "16px",
              borderRadius: "4px",
            }}
          >
            <div style={{ fontSize: "16px", lineHeight: "22px" }}>
              Compare Previously Published
            </div>
            <div
              style={{ fontSize: "14px", lineHeight: "20px", marginTop: "4px" }}
            >
              Compare differences between previously published PolicyJsons and
              then select one to request for restoring. Restoring will replace
              the current application data.
            </div>
          </Box>
          <Box
            sx={{
              maxHeight: "calc(100vh - 345px)",
              overflowY: "auto",
            }}
          >
            <Box
              sx={{
                background: "white",
                marginTop: "1rem",
                padding: "0.5rem 1rem",
              }}
            >
              <Box width={"100%"} display={"flex"}>
                <Box
                  width="50%"
                  padding={0}
                  display={"flex"}
                  alignItems="center"
                >
                  <RadioGroup
                    value={selectedApp}
                    onChange={(e) => setSelectedApp(e.target.value)}
                    aria-label="app1"
                    name="app1-radio-group"
                  >
                    <FormControlLabel
                      value="app1"
                      control={<Radio />}
                      label=""
                      sx={{ marginRight: 0.5 }}
                    />
                  </RadioGroup>
                  <Box width="100%">
                    <Typography>Published Application</Typography>
                    <Dropdown
                      options={publishedApplicationList?.filter(
                        (item: any) => item?.value !== Number(formData?.app2),
                      )}
                      aria-label="app1"
                      optionsTextLimit={200}
                      fullWidth
                      selectedOptions={
                        publishedApplicationList?.find(
                          (opt) => String(opt?.value) === formData?.app1,
                        )
                          ? [
                              publishedApplicationList?.find(
                                (opt) => String(opt?.value) === formData?.app1,
                              )!,
                            ]
                          : []
                      }
                      onChange={(selected: any[]) => {
                        setFormData({
                          ...formData,
                          app1: String(selected[0]?.value),
                        });
                      }}
                      style={{ width: "70%" }}
                    />
                  </Box>
                </Box>
                <Box
                  width="50%"
                  padding={1}
                  display={"flex"}
                  alignItems="center"
                >
                  <RadioGroup
                    value={selectedApp}
                    onChange={(e) => setSelectedApp(e.target.value)}
                    aria-label="app2"
                    name="app1-radio-group"
                  >
                    <FormControlLabel
                      value="app2"
                      control={<Radio />}
                      label=""
                      sx={{ marginRight: 0.5 }}
                    />
                  </RadioGroup>
                  <Box width="100%">
                    <Typography>Published Application</Typography>
                    <Dropdown
                      options={publishedApplicationList?.filter(
                        (item: any) => item?.value !== Number(formData?.app1),
                      )}
                      aria-label="app2"
                      optionsTextLimit={200}
                      fullWidth
                      selectedOptions={
                        publishedApplicationList?.find(
                          (opt) => String(opt?.value) === formData?.app2,
                        )
                          ? [
                              publishedApplicationList?.find(
                                (opt) => String(opt?.value) === formData?.app2,
                              )!,
                            ]
                          : []
                      }
                      onChange={(selected: any[]) => {
                        setFormData({
                          ...formData,
                          app2: String(selected[0]?.value),
                        });
                      }}
                      style={{ width: "70%" }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
            {formData?.app1 && formData?.app2 && (
              <Box
                sx={{
                  background: "white",
                  marginTop: "1rem",
                  padding: "1rem",
                }}
              >
                <RoleComparison
                  url={`/publish/compare-versions`}
                  payload={{
                    versionIdA: formData?.app1,
                    versionIdB: formData?.app2,
                  }}
                  app1Name={
                    publishedApplicationList?.find(
                      (opt) => String(opt?.value) === formData?.app1,
                    )?.label
                  }
                  app2Name={
                    publishedApplicationList?.find(
                      (opt) => String(opt?.value) === formData?.app2,
                    )?.label
                  }
                />
              </Box>
            )}
            {formData?.app1 && formData?.app2 && (
              <Box
                sx={{
                  background: "white",
                  marginTop: "1rem",
                  padding: "1rem",
                  overflowY: "auto",
                }}
              >
                <TextArea
                  fullWidth
                  placeholder="Input Text"
                  label="Comment"
                  value={contextForm?.comment}
                  onChange={(e) => handleChange("comment", e?.target?.value)}
                  maxLength={100}
                />
              </Box>
            )}
          </Box>
        </Box>
      </div>

      <div
        style={{
          backgroundColor: "white",
          position: "fixed",
          width: "100%",
          padding: "10px 0",
          bottom: 0,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        <Button
          variant="tertiary"
          sx={{ margin: "0 10px" }}
          size="medium"
          onClick={() =>
            navigate(`/application-list/app-${data?.id}`, {
              state: { selectedTab: 9 },
            })
          }
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          sx={{ margin: "0 10px" }}
          size="medium"
          onClick={async () => {
            try {
              setIsLoading(true);
              if (selectedApp) await restorePublishVersion();
            } catch (err) {
            } finally {
              setIsLoading(false);
            }
          }}
          disabled={isFormValid()}
        >
          Request Approval
        </Button>
      </div>
      {isLoading ? (
        <div className={scssClass?.loaderSpinner}>
          <LoadingSpinner />
        </div>
      ) : null}
    </>
  );
};

export default CompareApplication;
